import { lazy, Suspense, useEffect } from "react";
import NotFoundPage from "./pages/NotFoundPage";
import { PrivateRoute, Toaster } from "./components";
import { LazyRouteBoundary } from "./components/lazy-route-boundary";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LoadingSpinner } from "./components/loading-spinner.tsx";
import useNavigatorOnline from "./hooks/use-navigator-online.ts";
import { retryOfflineRequests } from "./utilities/offline-db.ts";
import {
  canRunOfflineSync,
  waitForSyncPrerequisites,
} from "./utilities/sync-session.utils.ts";
import { useSessionStore } from "./store/session.store.ts";
import { SyncMonitor } from "./components/sync-monitor.tsx";
import { SyncBubble } from "./components/sync-bubble.tsx";
import { ThemeProvider } from "./components/theme-provider.tsx";
import { CajaCerradaNoticeHost } from "./components/caja-cerrada-notice-host.tsx";

const HomeLayout = lazy(() => import("./pages/home/layout"));
const PointOfSaleLayout = lazy(() => import("./pages/point-of-sale/layout"));
const OthersLayout = lazy(() => import("./pages/[others]/layout.tsx"));

const HomePage = lazy(() => import("./pages/home/page"));
const LoginPage = lazy(() => import("./pages/login/page"));
const OpenCashierPage = lazy(() => import("./pages/home/pages/cashiers/page"));
const ProductsPage = lazy(
  () => import("./pages/point-of-sale/pages/products/page")
);
const StoresPage = lazy(() => import("./pages/home/pages/stores/page"));
const CloseCashierPage = lazy(
  () => import("./pages/[others]/pages/close-cashier/page")
);
const SalesPage = lazy(() => import("./pages/[others]/pages/sales/page"));
const DashboardPage = lazy(
  () => import("./pages/[others]/pages/dashboard/page")
);
const PointOfSalePage = lazy(() => import("./pages/point-of-sale/page"));

function App() {
  const { isOnline } = useNavigatorOnline();
  const account = useSessionStore((state) => state.account);

  const isAdmin = account?.role === "ADMINISTRADOR";

  useEffect(() => {
    if (!isOnline) return;

    let cancelled = false;

    const runSync = async () => {
      await waitForSyncPrerequisites();
      if (cancelled || !canRunOfflineSync()) return;
      await retryOfflineRequests();
    };

    void runSync();

    const interval = setInterval(() => {
      void runSync();
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOnline]);

  return (
    <ThemeProvider>
      <Toaster
        toastOptions={{
          classNames: {
            default: "bg-white border-input",
            error: "text-destructive",
            success: "text-green-400",
            warning: "text-yellow-400",
            info: "text-primary",
          },
          duration: 1500,
        }}
        position="top-right"
        visibleToasts={1}
        expand
      />
      <CajaCerradaNoticeHost />
      <BrowserRouter>
        <LazyRouteBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <HomeLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="stores" element={<StoresPage />} />
              <Route path="cashiers/:id/open" element={<OpenCashierPage />} />
            </Route>
            <Route
              path="/point-of-sale"
              element={
                <PrivateRoute>
                  <PointOfSaleLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<PointOfSalePage />} />
              <Route path="products" element={<ProductsPage />} />
            </Route>
            <Route
              element={
                <PrivateRoute>
                  <OthersLayout />
                </PrivateRoute>
              }
            >
              <Route
                path="/cashiers/:id/close"
                element={<CloseCashierPage />}
              />
              <Route path="/sales" element={<SalesPage />} />
              {isAdmin && (
                <Route path="/dashboard" element={<DashboardPage />} />
              )}
            </Route>
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </LazyRouteBoundary>
      </BrowserRouter>

      {/* Componentes globales de sincronización */}
      <SyncMonitor />
      <SyncBubble />
    </ThemeProvider>
  );
}

export default App;
